import { useGetToolUsageQuery, useIsOwnerQuery } from "@/store/services/supabase";
import { ArrowUpOutlined } from "@ant-design/icons";
import { Alert, Button, Divider, Modal, Progress, Statistic } from "antd";
import Link from "next/link";
import { PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Markdown, { Components } from "react-markdown";
import styles from "./ScreeningLimitReview.module.scss";
export interface ScreeningUploadReviewProps extends PropsWithChildren {
  title: string;
  isOpen: boolean;
  continueText: string;
  onContinue: () => void;
  uploadedAssetCount: number;
  onBack: () => void;
}
export const ScreeningStatsRow = ({
  title,
  children
}: {
  title?: string;
  children: ReactNode;
}) => <div className={styles.statsRowContainer} data-sentry-component="ScreeningStatsRow" data-sentry-source-file="ScreeningLimitReview.tsx">
    {title && <div className={styles.statsTitle}>{title}</div>}
    {children && <div className={styles.statsValue}>{children}</div>}
  </div>;
export const ScreeningLimitReview = ({
  title,
  isOpen,
  continueText,
  onContinue,
  uploadedAssetCount,
  onBack,
  children
}: ScreeningUploadReviewProps) => {
  const {
    data: isOwner
  } = useIsOwnerQuery();
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "comprehensive.portfolioInsights.uploadLimitAlert"
  });
  const {
    data: toolUsage
  } = useGetToolUsageQuery({
    activeQuotaOnly: true
  });
  if (!toolUsage) return <></>;
  const currentUsage = toolUsage.singleQuotaMode && toolUsage.singleQuotaUsage ? toolUsage.singleQuotaUsage : toolUsage.screening;
  const uploadedAssetPercent = uploadedAssetCount / currentUsage.quota * 100;
  const futureUsage = currentUsage.usage + uploadedAssetCount;
  const futureUsagePercent = futureUsage / currentUsage.quota * 100;
  const showWarning = futureUsagePercent > 80 && futureUsagePercent <= 100;
  const showError = futureUsagePercent > 100;
  const exceedsQuotaBuffer = parseFloat(futureUsagePercent.toFixed(2)) > currentUsage.hardLimitPercent;
  const components: Components = {
    a: props => {
      return <Link href={props.href || ""} target="_blank">
          {props.children}
        </Link>;
    }
  };
  return <Modal title={title} open={isOpen} onClose={onBack} onCancel={onBack} footer={null} width={560} wrapClassName={styles.reviewScreeningContainer} data-sentry-element="Modal" data-sentry-component="ScreeningLimitReview" data-sentry-source-file="ScreeningLimitReview.tsx">
      <fieldset className={styles.reviewContainer}>
        <legend>Review Upload</legend>
        <div className={styles.statsContainer}>
          {children}

          <Divider plain orientation="left" style={{
          margin: 0,
          marginTop: "2em",
          marginBottom: "0.5em",
          color: "var(--color-gray-400)"
        }} orientationMargin={"0"} data-sentry-element="Divider" data-sentry-source-file="ScreeningLimitReview.tsx">
            Usage After Upload
          </Divider>

          <ScreeningStatsRow title="Assets Used" data-sentry-element="ScreeningStatsRow" data-sentry-source-file="ScreeningLimitReview.tsx">
            <Statistic value={futureUsage} className={`${styles.statsValue} ${showWarning && styles.warning} ${showError && styles.error}`} suffix={`/ ${currentUsage.quota?.toLocaleString() ?? "<Limit Not Set>"}`} data-sentry-element="Statistic" data-sentry-source-file="ScreeningLimitReview.tsx" />
            <Statistic value={uploadedAssetCount} valueStyle={{
            color: "var(--color-gray-400)"
          }} prefix={<ArrowUpOutlined />} className={styles.statsValue} data-sentry-element="Statistic" data-sentry-source-file="ScreeningLimitReview.tsx" />
          </ScreeningStatsRow>

          <ScreeningStatsRow title="Assets Used%" data-sentry-element="ScreeningStatsRow" data-sentry-source-file="ScreeningLimitReview.tsx">
            <Statistic value={futureUsagePercent} precision={2} suffix="%" className={`${styles.statsValue} ${showWarning && styles.warning} ${showError && styles.error}`} data-sentry-element="Statistic" data-sentry-source-file="ScreeningLimitReview.tsx" />
            <Statistic value={uploadedAssetPercent} precision={2} valueStyle={{
            color: "var(--color-gray-400)"
          }} prefix={<ArrowUpOutlined />} suffix="%" className={styles.statsValue} data-sentry-element="Statistic" data-sentry-source-file="ScreeningLimitReview.tsx" />
          </ScreeningStatsRow>

          <ScreeningStatsRow data-sentry-element="ScreeningStatsRow" data-sentry-source-file="ScreeningLimitReview.tsx">
            <Progress percent={futureUsagePercent} success={{
            percent: currentUsage.usagePercent,
            strokeColor: "#4B1553"
          }} className={`${styles.progressContainer} ${showError && styles.error}`} showInfo={false} style={{
            gridColumn: "2 / 4"
          }} data-sentry-element="Progress" data-sentry-source-file="ScreeningLimitReview.tsx" />
          </ScreeningStatsRow>
        </div>
      </fieldset>
      {showWarning && <Alert message={<Markdown components={components}>{isOwner ? t("owner.warning") : t("user.warning")}</Markdown>} type="warning" showIcon className="mt-4" />}

      {showError && <Alert message={<div>
              <div>
                <strong>Limit Reached</strong>
              </div>
              <Markdown components={components}>{isOwner ? t("owner.error") : t("user.error")}</Markdown>
            </div>} type="error" showIcon className="mt-4" />}
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button type="text" onClick={onBack} data-sentry-element="Button" data-sentry-source-file="ScreeningLimitReview.tsx">
          Back
        </Button>
        <Button disabled={currentUsage.alwaysEnabled ? false : currentUsage.enabled ? exceedsQuotaBuffer : true} onClick={onContinue} type="primary" data-sentry-element="Button" data-sentry-source-file="ScreeningLimitReview.tsx">
          {continueText}
        </Button>
      </div>
    </Modal>;
};